import React, { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { DatePickerWithRange } from "@/components/ui/date-range/date-rangePicker";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import TableFilters from "@/components/ui/table/data-table-filter";
import { paymentStatusValues } from "@/lib/constants/payment_report";
import { useGetAllExtensionsQuery } from "@/services/extensionApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

interface PaymentReportFiltersProps {
  dateRange?: {
    start_date?: string;
    end_date?: string;
  };
  appliedDateRange?: {
    start_date?: string;
    end_date?: string;
  };
  onDateRangeChange?: (dates: {
    start_date: Date | undefined;
    end_date: Date | undefined;
  }) => void;
  onResetFilters?: () => void;
  onApplyFilters?: () => void;
  filterData: Record<string, any>;
  setFilterData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  openFilter: boolean;
  setOpenFilter: React.Dispatch<React.SetStateAction<boolean>>;
  initialValue: any;
  showAdvancedFilters?: boolean;
}

export default function PaymentReportFilters({
  dateRange,
  appliedDateRange,
  onDateRangeChange,
  onResetFilters,
  onApplyFilters,
  filterData,
  setFilterData,
  openFilter,
  setOpenFilter,
  initialValue,
  showAdvancedFilters = true
}: PaymentReportFiltersProps) {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  // Data queries
  const { data: enabledPayments } = useGetAllExtensionsQuery({ 
    org_id: orgId, 
    query: `type=payment&is_active=true` 
  });

  // Process options
  const enabledPaymentsOptions = useMemo(() => {
    return Array.isArray(enabledPayments?.data) 
      ? enabledPayments?.data.map((item) => ({
        value: item.id,
        label: item.name,
      })) 
      : [];
  }, [enabledPayments]);

  function handleFilterChange(field: string, value: string | number | (string | number)[]) {
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  const filterDisplay = useMemo(() => [
    {
      type: "multiselect",
      name: "status",
      label: "Status",
      options: paymentStatusValues.map((item) => ({
        value: item.value,
        label: item.label,
      })),
      function: (value: (string | number)[]) => handleFilterChange("status", value),
    },
    {
      type: "multiselect",
      name: "payment_method_ids",
      label: "Payment Method",
      options: enabledPaymentsOptions,
      function: (value: (string | number)[]) => {
        // Keep as array - parent will handle converting to query params
        handleFilterChange("payment_method_ids", value);
      },
    }
  ], [paymentStatusValues, enabledPaymentsOptions]);

  const handleApplyFiltersClick = () => {
    onApplyFilters?.(); 
  };

  const resetFilters = () => {
    setFilterData({});
    onResetFilters?.();
  };

  return (
    <>
      {/* Main Filter Controls */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-row gap-2 w-full items-center">
            {/* DatePicker - original fixed width on desktop, stretches on mobile */}
            <div className="w-full sm:w-auto sm:flex-none">
              <DatePickerWithRange
                name={"Date Range"}
                value={{
                  start_date: dateRange?.start_date ? new Date(dateRange.start_date) : undefined,
                  end_date: dateRange?.end_date ? new Date(dateRange.end_date) : undefined,
                }}
                onValueChange={(dates) => {
                  onDateRangeChange?.(dates);
                }}
                label={"Select date range"}
                className="w-full sm:w-[240px] h-8"
              />
            </div>

            {/* Buttons - icon-only on mobile, full text on desktop */}
            <div className="flex flex-row gap-2 flex-shrink-0">
              <Button
                variant={"default"}
                className="bg-primary text-black h-8 flex items-center gap-2 px-3"
                onClick={handleApplyFiltersClick}
              >
                <i className="fa fa-filter text-sm"></i>
                <span className="hidden sm:inline text-sm">Apply</span>
              </Button>

              <Button
                variant={"outline"}
                className="text-black h-8 flex items-center gap-2 px-3"
                onClick={resetFilters}
              >
                <i className="fa-solid fa-arrows-rotate text-sm"></i>
                <span className="hidden sm:inline text-sm">Reset</span>
              </Button>

              {showAdvancedFilters && (
                <button
                  className="border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center flex-shrink-0"
                  onClick={() => setOpenFilter(true)}
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <i className="fa fa-filter"></i>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Advanced filters</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Filters Modal */}
      {showAdvancedFilters && (
        <TableFilters
          isOpen={openFilter}
          setOpen={setOpenFilter}
          initialValue={initialValue}
          filterData={filterData}
          setFilter={setFilterData}
          setSearchCriteria={(value: any) => {
            // When user clicks "Apply filters" in the modal, call parent's apply function
            if (typeof value === 'function') {
              setFilterData(value);
            } else {
              setFilterData(value);
            }
            handleApplyFiltersClick();
          }}
          filterDisplay={filterDisplay}
          setInputValue={() => {}} // Not used in this context
          onReset={resetFilters}
        />
      )}
    </>
  );
}
